import { useEffect, useRef, useState } from 'react';
import { Download, Keyboard, RotateCcw, Upload, X } from 'lucide-react';
import {
  formatShortcut,
  shortcutsEqual,
  type CommandRegistry,
  type CommandShortcut,
  type CommandSpec,
} from '../lib/command-registry';
import { useCommandRegistryVersion } from '../hooks/useCommandRegistry';
import { captureCombo, isCapturedShortcut, CAPTURE_CANCELLED } from '../lib/combo-recorder';
import type { KeyboardOverrides } from '../lib/keyboard-overrides';
import { toast } from '@/components/ui/use-toast';

/** Settings → Keyboard dialog (Tier 3 Group C Phase 2). Lets the
 *  user remap any non-hidden editor command. Persistence is the
 *  caller's job — this component only mutates the registry +
 *  notifies via `onOverridesChanged`; EditorPage saves the result
 *  to localStorage.
 *
 *  Conflict policy (v1): refuse a remap that collides with another
 *  command's existing binding and surface the conflict inline. The
 *  user can manually clear the conflicting command first, then
 *  re-attempt the assignment. Photoshop-style "auto-clear-loser-
 *  with-warning" is a future enhancement once we have a toast +
 *  undo affordance to soften the destructive action.
 *
 *  Export / Import JSON uses the same shape as the localStorage
 *  payload (KeyboardOverrides). Import validates defensively — bad
 *  shapes are dropped, not thrown. */
export interface KeyboardSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  registry: CommandRegistry;
  isMac: boolean;
  /** Fired after every successful change. Caller persists the new
   *  map (typically `saveKeyboardOverrides(map)` to localStorage).
   *  The map reflects the registry's current overrides, so missing
   *  entries mean "use default." */
  onOverridesChanged: (overrides: KeyboardOverrides) => void;
}

export function KeyboardSettingsDialog({
  open,
  onClose,
  registry,
  isMac,
  onOverridesChanged,
}: KeyboardSettingsDialogProps) {
  // Subscribe to registry mutations — re-renders on every change
  // (register, unregister, setShortcutOverrides). Replaces the
  // earlier `useMemo([registry, filter])` pattern that snapshotted
  // commands once at mount and went stale if the registry mutated
  // afterward. The empty-list-on-first-open bug came from that
  // snapshot running before all commands were registered.
  useCommandRegistryVersion(registry);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  // `conflictById` is keyed by command id; the value is the spec of
  // the OTHER command currently holding the combo. Cleared on edit
  // start / commit / cancel.
  const [conflictById, setConflictById] = useState<Record<string, CommandSpec | null>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset transient UI state on open. Override snapshot is read
  // inline from the registry every render — no separate state to
  // keep in sync.
  useEffect(() => {
    if (open) {
      setFilter('');
      setEditingId(null);
      setConflictById({});
    }
  }, [open]);

  // Read live every render. ~44 commands × hidden filter is cheap;
  // memoization was the source of the empty-list-on-first-open bug.
  const overrides = registry.getAllShortcutOverrides();
  const all = registry.list().filter((c) => !c.hidden);
  const q = filter.trim().toLowerCase();
  const visible =
    q.length === 0 ? all : all.filter((c) => c.label.toLowerCase().includes(q) || c.id.includes(q));

  // Group by category, preserving each group's first-encountered
  // order (matches buildEditorCommands' declaration order).
  const grouped: Array<{ category: string; commands: CommandSpec[] }> = [];
  const indexByCategory = new Map<string, number>();
  for (const c of visible) {
    let i = indexByCategory.get(c.category);
    if (i === undefined) {
      i = grouped.length;
      indexByCategory.set(c.category, i);
      grouped.push({ category: c.category, commands: [] });
    }
    grouped[i].commands.push(c);
  }

  function commitChange(id: string, shortcuts: readonly CommandShortcut[] | null) {
    registry.setShortcutOverrides(id, shortcuts);
    onOverridesChanged(registry.getAllShortcutOverrides());
  }

  function tryAssignCombo(id: string, combo: CommandShortcut) {
    const conflict = registry.findShortcutConflict(combo, id);
    if (conflict) {
      setConflictById((m) => ({ ...m, [id]: conflict }));
      setEditingId(null);
      return;
    }
    setConflictById((m) => ({ ...m, [id]: null }));
    commitChange(id, [combo]);
    setEditingId(null);
  }

  function resetCommand(id: string) {
    commitChange(id, null);
    setConflictById((m) => ({ ...m, [id]: null }));
  }

  function clearCommand(id: string) {
    commitChange(id, []);
    setConflictById((m) => ({ ...m, [id]: null }));
  }

  function resetAll() {
    registry.clearShortcutOverrides();
    onOverridesChanged(registry.getAllShortcutOverrides());
    setConflictById({});
  }

  function exportOverrides() {
    const json = JSON.stringify(registry.getAllShortcutOverrides(), null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'image-editor-keyboard-overrides.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function triggerImport() {
    fileInputRef.current?.click();
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input so re-importing the same file re-triggers
    // the onChange (browsers debounce identical selections).
    e.target.value = '';
    if (!file) return;
    file
      .text()
      .then((text) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(text);
        } catch {
          toast({
            title: 'Import failed',
            description: 'The file is not valid JSON.',
            variant: 'destructive',
          });
          return;
        }
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          toast({
            title: 'Import failed',
            description: 'Expected a JSON object of command-id → shortcuts.',
            variant: 'destructive',
          });
          return;
        }
        // Validate against the registry's known command ids; drop
        // unknown ids so a stale import doesn't introduce ghost
        // entries that would never dispatch. Track skipped count
        // for the toast so the user knows whether the file was a
        // partial / outdated export.
        const tentative: KeyboardOverrides = {};
        let malformedCount = 0;
        for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
          if (!registry.get(id)) {
            malformedCount++;
            continue;
          }
          if (!Array.isArray(value)) {
            malformedCount++;
            continue;
          }
          const shortcuts: CommandShortcut[] = [];
          for (const s of value) {
            if (!s || typeof s !== 'object') continue;
            const sc = s as Record<string, unknown>;
            if (typeof sc.key !== 'string' || sc.key.length === 0) continue;
            const out: CommandShortcut = { key: sc.key };
            if (typeof sc.code === 'string') out.code = sc.code;
            if (sc.mod === true) out.mod = true;
            if (sc.ctrl === true) out.ctrl = true;
            if (sc.shift === true) out.shift = true;
            if (sc.alt === true) out.alt = true;
            shortcuts.push(out);
          }
          tentative[id] = shortcuts;
        }

        // ─── Conflict detection (iterative until stable) ────────
        // setAllShortcutOverrides clears every override first, so
        // commands NOT in the import revert to their declared
        // defaults. A combo collision arises when two commands'
        // effective shortcuts overlap in the final state. Policy:
        //
        //   - Both commands in the import → drop the LATER (by JSON
        //     order), preserving the earlier intent.
        //   - One in the import, one not → drop the import entry
        //     (preserves existing default functionality).
        //   - Neither in the import (default-default collision) →
        //     can't fix without dropping a default; left as-is.
        //
        // Iteration is required because dropping an import entry
        // reverts its command to its DEFAULT shortcut, which may
        // create a new collision with another import entry. Without
        // iteration, the final state can still contain duplicates.
        // The pre-PR review caught this cascading case.
        const importIdOrder = new Map<string, number>();
        Object.keys(tentative).forEach((id, i) => importIdOrder.set(id, i));
        const acceptedIds = new Set<string>(Object.keys(tentative));
        const conflictReports: Array<{ label: string; conflictsWith: string }> = [];

        const prospectiveFor = (id: string): readonly CommandShortcut[] => {
          if (acceptedIds.has(id)) return tentative[id];
          return registry.get(id)?.shortcuts ?? [];
        };

        const allCmdIds = registry.list().map((c) => c.id);
        // Each iteration drops at most one entry. The set of import
        // entries shrinks monotonically — bounded by the import size,
        // so termination is guaranteed without an explicit cap.
        let changed = true;
        while (changed) {
          changed = false;
          outer: for (let i = 0; i < allCmdIds.length; i++) {
            for (let j = i + 1; j < allCmdIds.length; j++) {
              const aId = allCmdIds[i];
              const bId = allCmdIds[j];
              const sa = prospectiveFor(aId);
              const sb = prospectiveFor(bId);
              if (sa.length === 0 || sb.length === 0) continue;
              for (const x of sa) {
                for (const y of sb) {
                  if (!shortcutsEqual(x, y)) continue;
                  const aIsImport = acceptedIds.has(aId);
                  const bIsImport = acceptedIds.has(bId);
                  let dropId: string | null = null;
                  let keepId: string | null = null;
                  if (aIsImport && bIsImport) {
                    // Both imports — later JSON order loses.
                    if ((importIdOrder.get(aId) ?? 0) > (importIdOrder.get(bId) ?? 0)) {
                      dropId = aId;
                      keepId = bId;
                    } else {
                      dropId = bId;
                      keepId = aId;
                    }
                  } else if (aIsImport) {
                    dropId = aId;
                    keepId = bId;
                  } else if (bIsImport) {
                    dropId = bId;
                    keepId = aId;
                  } else {
                    // Pre-existing default-default collision — leave
                    // as-is (no import entry to drop).
                    continue;
                  }
                  acceptedIds.delete(dropId);
                  conflictReports.push({
                    label: registry.get(dropId)?.label ?? dropId,
                    conflictsWith: registry.get(keepId)?.label ?? keepId,
                  });
                  changed = true;
                  break outer;
                }
              }
            }
          }
        }

        const accepted: KeyboardOverrides = {};
        for (const id of acceptedIds) {
          accepted[id] = tentative[id];
        }

        registry.setAllShortcutOverrides(accepted);
        onOverridesChanged(registry.getAllShortcutOverrides());
        setConflictById({});

        const importedCount = Object.keys(accepted).length;
        const conflictCount = conflictReports.length;
        const parts: string[] = [];
        parts.push(`Imported ${importedCount} override${importedCount === 1 ? '' : 's'}.`);
        if (conflictCount > 0) {
          const list = conflictReports
            .slice(0, 3)
            .map((c) => `"${c.label}" → conflicts with "${c.conflictsWith}"`)
            .join('; ');
          const extra = conflictCount > 3 ? ` (+${conflictCount - 3} more)` : '';
          parts.push(
            `Skipped ${conflictCount} conflict${conflictCount === 1 ? '' : 's'}: ${list}${extra}.`,
          );
        }
        if (malformedCount > 0) {
          parts.push(
            `Skipped ${malformedCount} unknown / malformed entr${malformedCount === 1 ? 'y' : 'ies'}.`,
          );
        }
        // Conflicts are advisory (the import partially succeeded), not
        // destructive — leave the 'destructive' red variant for actual
        // errors (parse failure, file unreadable).
        toast({
          title:
            conflictCount > 0 || malformedCount > 0
              ? 'Import complete with warnings'
              : 'Import successful',
          description: parts.join(' '),
        });
      })
      .catch(() => {
        toast({
          title: 'Import failed',
          description: 'Could not read the selected file.',
          variant: 'destructive',
        });
      });
  }

  if (!open) return null;

  const overrideCount = Object.keys(overrides).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-label="Keyboard shortcuts"
        aria-modal="true"
        className="bg-background border border-border rounded-lg shadow-xl w-[92vw] max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
          <Keyboard className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-base font-semibold flex-1">Keyboard Shortcuts</h2>
          <button
            type="button"
            onClick={exportOverrides}
            title="Export overrides as JSON"
            aria-label="Export keyboard overrides as JSON"
            className="p-2 rounded hover:bg-accent text-muted-foreground hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring outline-none"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={triggerImport}
            title="Import overrides from JSON"
            aria-label="Import keyboard overrides from JSON"
            className="p-2 rounded hover:bg-accent text-muted-foreground hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring outline-none"
          >
            <Upload className="h-4 w-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleImportFile}
            className="hidden"
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded hover:bg-accent text-muted-foreground hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-border shrink-0">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter commands..."
            aria-label="Filter commands"
            className="w-full px-3 py-2 text-sm bg-muted/30 rounded border border-border outline-none focus:ring-1 focus:ring-ring"
          />
        </div>

        <div className="overflow-y-auto flex-1">
          {grouped.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">
              No commands match "{filter}".
            </div>
          ) : (
            grouped.map((group) => (
              <section key={group.category} className="py-2">
                <div
                  role="heading"
                  aria-level={3}
                  className="px-4 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
                >
                  {group.category}
                </div>
                {group.commands.map((cmd) => (
                  <ShortcutFieldRow
                    key={cmd.id}
                    command={cmd}
                    isMac={isMac}
                    isEditing={editingId === cmd.id}
                    isModified={cmd.id in overrides}
                    conflict={conflictById[cmd.id] ?? null}
                    effective={registry.getEffectiveShortcuts(cmd.id)}
                    onStartEdit={() => {
                      setEditingId(cmd.id);
                      setConflictById((m) => ({ ...m, [cmd.id]: null }));
                    }}
                    onCommit={(combo) => tryAssignCombo(cmd.id, combo)}
                    onCancelEdit={() => setEditingId(null)}
                    onReset={() => resetCommand(cmd.id)}
                    onClear={() => clearCommand(cmd.id)}
                  />
                ))}
              </section>
            ))
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-border shrink-0">
          <span className="text-xs text-muted-foreground">
            {overrideCount === 0
              ? 'No overrides — using defaults.'
              : `${overrideCount} override${overrideCount === 1 ? '' : 's'} active.`}
          </span>
          <button
            type="button"
            onClick={resetAll}
            disabled={overrideCount === 0}
            className="text-xs px-3 py-1.5 rounded border border-border hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 focus-visible:ring-1 focus-visible:ring-ring outline-none"
          >
            <RotateCcw className="h-3 w-3" />
            Reset All
          </button>
        </div>
      </div>
    </div>
  );
}

interface ShortcutFieldRowProps {
  command: CommandSpec;
  isMac: boolean;
  isEditing: boolean;
  isModified: boolean;
  conflict: CommandSpec | null;
  effective: readonly CommandShortcut[];
  onStartEdit: () => void;
  onCommit: (combo: CommandShortcut) => void;
  onCancelEdit: () => void;
  onReset: () => void;
  onClear: () => void;
}

function ShortcutFieldRow({
  command,
  isMac,
  isEditing,
  isModified,
  conflict,
  effective,
  onStartEdit,
  onCommit,
  onCancelEdit,
  onReset,
  onClear,
}: ShortcutFieldRowProps) {
  // Keydown handler for the recorder. Wired only while editing.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const result = captureCombo(e.nativeEvent, isMac);
    if (result === null) return;
    e.preventDefault();
    if (result === CAPTURE_CANCELLED) {
      onCancelEdit();
      return;
    }
    if (isCapturedShortcut(result)) {
      onCommit(result);
    }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2 hover:bg-accent/40">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm truncate">{command.label}</span>
          {isModified && (
            <span
              className="text-[10px] uppercase tracking-wide text-muted-foreground"
              title="Customized from default"
            >
              Modified
            </span>
          )}
        </div>
        {conflict && (
          // role="alert" forces screen readers to announce the
          // conflict the moment it appears — without it, the
          // recording field silently reverts and SR users don't know
          // why.
          <div role="alert" className="text-xs text-destructive mt-0.5">
            Conflicts with "{conflict.label}" — clear that binding first.
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onStartEdit}
          onKeyDown={isEditing ? handleKeyDown : undefined}
          onBlur={isEditing ? onCancelEdit : undefined}
          // aria-pressed exposes the toggle state to screen readers
          // — without it the only signal is the aria-label change,
          // which not all readers re-announce on focus.
          aria-pressed={isEditing}
          aria-label={
            isEditing
              ? `Press a key combo to set shortcut for ${command.label}, or Escape to cancel`
              : `Edit shortcut for ${command.label}`
          }
          className={[
            'min-w-[120px] text-right px-2 py-1 rounded border text-xs font-mono',
            isEditing
              ? 'border-ring bg-accent text-foreground'
              : 'border-border bg-muted/30 text-muted-foreground hover:text-foreground',
          ].join(' ')}
        >
          {isEditing ? (
            'Press keys...'
          ) : effective.length === 0 ? (
            <em className="not-italic">unassigned</em>
          ) : (
            effective.map((s) => formatShortcut(s, isMac)).join(' / ')
          )}
        </button>
        {isModified && (
          <button
            type="button"
            onClick={onReset}
            title="Reset to default"
            aria-label={`Reset shortcut for ${command.label} to default`}
            className="p-2 rounded text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring outline-none"
          >
            <RotateCcw className="h-3 w-3" />
          </button>
        )}
        {effective.length > 0 && !isEditing && (
          <button
            type="button"
            onClick={onClear}
            title="Unbind this command"
            aria-label={`Unbind shortcut for ${command.label}`}
            className="p-2 rounded text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring outline-none"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}
